import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Ban, Trash2, Check, X, FileText } from "lucide-react";
import { useAuthedApi, errMsg, imgUrl } from "../../lib/api";
import DealerLogo from "../DealerLogo";
import { PrivateDocButton } from "../PrivateDoc";

const STATUS = { verified: "text-emerald-400", pending: "text-amber-400", rejected: "text-red-400", none: "text-gray-500" };

export default function AdminDealers() {
  const api = useAuthedApi();
  const [filter, setFilter] = useState("");
  const [list, setList] = useState(null);
  const load = () => { api("get", "/admin/dealers", null, { params: { status: filter || undefined } }).then(setList); };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); } };
  const decide = (d, approve) => {
    const reason = approve ? "" : window.prompt("Reason for rejection (shown to the dealer):", "") ?? null;
    if (reason === null) return;
    act(() => api("post", `/admin/dealers/${d.id}/verification`, { approve, reason }), approve ? "Dealer verified" : "Verification rejected");
  };

  return (
    <div className="space-y-5" data-testid="admin-dealers">
      <div className="flex flex-wrap gap-2">
        {[["", "All"], ["pending", "Pending verification"], ["verified", "Verified"], ["rejected", "Rejected"], ["none", "Not requested"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} className={`px-4 py-2 rounded-full text-xs font-semibold ${filter === v ? "bg-[#D4AF37] text-black" : "border border-white/10 text-gray-300"}`} data-testid={`admin-dealers-filter-${v || "all"}`}>{l}</button>
        ))}
      </div>
      {list && !list.length && <div className="lux-card p-12 text-center text-gray-400">No dealers here.</div>}
      <div className="grid lg:grid-cols-2 gap-4">
        {list?.map((d) => (
          <div key={d.id} className={`lux-card p-5 space-y-4 ${d.suspended ? "opacity-60" : ""}`} data-testid={`admin-dealer-${d.id}`}>
            <div className="flex gap-4 items-start">
              <DealerLogo dealer={d} size="w-14 h-14" text="text-xl" />
              <div className="flex-1 min-w-0">
                <Link to={`/dealers/${d.id}`} className="font-semibold flex items-center gap-1.5 hover:text-[#E5C158]">{d.name}{d.verified && <BadgeCheck className="w-4 h-4 text-[#D4AF37]" />}</Link>
                <p className="text-xs text-gray-400">{d.city}, {d.state} · {d.phone} · {d.inventory} cars{d.is_demo && " · demo"}</p>
                <p className="text-xs mt-1">Verification: <span className={STATUS[d.verification_status || "none"]} data-testid={`admin-dealer-status-${d.id}`}>{d.verification_status || "none"}</span>{d.suspended && <span className="text-red-400 ml-2">· suspended</span>}</p>
              </div>
            </div>
            {d.verification && (
              <div className="rounded-xl bg-black/40 border border-white/5 p-4 text-xs space-y-1.5" data-testid={`admin-dealer-docs-${d.id}`}>
                <p><span className="text-gray-500">GST:</span> {d.verification.gst_number}</p>
                {d.verification.trade_licence && <p><span className="text-gray-500">Trade licence:</span> {d.verification.trade_licence}</p>}
                {d.verification.note && <p className="italic text-gray-300">"{d.verification.note}"</p>}
                {d.verification.document_url && (d.verification.document_url.startsWith("/api/private-files/")
                  ? <PrivateDocButton url={d.verification.document_url} testid={`admin-dealer-doc-${d.id}`} />
                  : <a href={imgUrl(d.verification.document_url)} target="_blank" rel="noreferrer" className="text-[#E5C158] flex items-center gap-1"><FileText className="w-3 h-3" />View document</a>)}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {!d.verified && <button className="px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold flex items-center gap-1" onClick={() => decide(d, true)} data-testid={`admin-approve-${d.id}`}><Check className="w-3 h-3" />Approve</button>}
              {(d.verified || d.verification_status === "pending") && <button className="btn-outline !px-3 !py-1.5 text-xs" onClick={() => decide(d, false)} data-testid={`admin-reject-${d.id}`}><X className="w-3 h-3" />{d.verified ? "Revoke badge" : "Reject"}</button>}
              <button className="btn-outline !px-3 !py-1.5 text-xs" onClick={() => act(() => api("patch", `/admin/dealers/${d.id}/suspend`, { suspended: !d.suspended }), d.suspended ? "Dealer restored" : "Dealer blocked — store and cars hidden")} data-testid={`admin-suspend-${d.id}`}><Ban className="w-3 h-3" />{d.suspended ? "Unblock" : "Block"}</button>
              <button className="btn-outline !px-3 !py-1.5 text-xs !text-red-400 !border-red-500/40" onClick={() => window.confirm(`Delete ${d.name} and all its cars?`) && act(() => api("delete", `/admin/dealers/${d.id}`), "Dealer deleted")} data-testid={`admin-delete-dealer-${d.id}`}><Trash2 className="w-3 h-3" />Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
